import { useEffect, useMemo, useState } from "react";
import { Globe, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useLanguage } from "@/context/LanguageContext";

// Languages pinned to the top of the list for quick access.
const POPULAR = ["gd", "en", "de", "fr", "es", "it", "pt-PT"];

export function LanguageSelector() {
  const { lang, changeLang, languages } = useLanguage();
  const [open, setOpen] = useState(false);
  const [hideSearch, setHideSearch] = useState(false);
  const current = languages.find((l) => l.code === lang);

  // While the selector is open, hide the search bar once the visitor scrolls the page.
  useEffect(() => {
    if (!open) return undefined;
    setHideSearch(false);
    const onScroll = () => setHideSearch(true);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [open]);

  const { popular, rest } = useMemo(() => {
    const byCode = Object.fromEntries(languages.map((l) => [l.code, l]));
    const pop = POPULAR.map((c) => byCode[c]).filter(Boolean);
    const others = languages
      .filter((l) => !POPULAR.includes(l.code))
      .sort((a, b) => a.native.localeCompare(b.native));
    return { popular: pop, rest: others };
  }, [languages]);

  const headingCls =
    "[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:font-mono [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-[0.2em] [&_[cmdk-group-heading]]:text-clan-muted";

  const renderItem = (l) => (
    <CommandItem
      key={l.code}
      value={`${l.native} ${l.label} ${l.short} ${l.code}`}
      data-testid={`lang-option-${l.code}`}
      onSelect={() => {
        changeLang(l.code);
        setOpen(false);
      }}
      className="cursor-pointer justify-between font-sans text-sm text-clan-ink data-[selected=true]:bg-clan-accent/15 data-[selected=true]:text-clan-ink"
    >
      <span className="flex items-center gap-2">
        {l.native}
        <span className="font-mono text-[10px] uppercase tracking-widest text-clan-muted">
          {l.short}
        </span>
      </span>
      {l.code === lang && <Check className="h-3.5 w-3.5 text-clan-accent" />}
    </CommandItem>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="lang-switcher-trigger"
          aria-label="Select language"
          className="flex items-center gap-1.5 rounded-md border border-clan-ink/15 bg-clan-ink/[0.04] px-2.5 py-1.5 font-mono text-xs uppercase tracking-widest text-clan-ink backdrop-blur-md transition-colors duration-300 hover:border-clan-accent/60 hover:bg-clan-ink/[0.06] focus:outline-none focus:ring-2 focus:ring-clan-accent sm:gap-2 sm:px-3.5 sm:py-2"
        >
          <Globe className="hidden h-3.5 w-3.5 text-clan-accent sm:block" />
          <span className="sm:hidden">{(current?.short || "EN").split("-")[0]}</span>
          <span className="hidden sm:inline">{current?.short || "EN"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-[260px] overflow-hidden border-clan-ink/10 bg-clan-bg/95 p-0 text-clan-ink backdrop-blur-xl"
      >
        <Command
          className="bg-transparent"
          filter={(value, search) => (value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0)}
        >
          {!hideSearch && (
            <CommandInput
              data-testid="lang-search-input"
              placeholder="Search language…"
              className="text-clan-ink placeholder:text-clan-muted/70"
            />
          )}
          <CommandList data-lenis-prevent className="max-h-[340px] overscroll-contain">
            <CommandEmpty className="py-6 text-center text-sm text-clan-muted">
              No language found.
            </CommandEmpty>
            <CommandGroup heading="Popular" className={headingCls}>
              {popular.map(renderItem)}
            </CommandGroup>
            <CommandSeparator className="bg-clan-ink/10" />
            <CommandGroup heading="All languages" className={headingCls}>
              {rest.map(renderItem)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
